/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  fromPromise,
  InMemoryCache,
  PossibleTypesMap,
  ServerError
} from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { asyncMap } from '@apollo/client/utilities'
import Bugsnag from '@bugsnag/js'
import { createUploadLink } from 'apollo-upload-client'
import EnvironmentService from 'app/lib/services/EnvironmentService'

import {
  CSRF_REJECTION_STATUS,
  readXsrfToken,
  reportXsrfRecovery,
  storeXsrfToken,
  XSRF_REQUEST_HEADER_NAME,
  XSRF_RESPONSE_HEADER_NAME
} from '../xsrf'

const baseUrl = EnvironmentService.getHostUrlValue()

const XSRF_RETRIED_CONTEXT_KEY = 'xsrfRetried'

const errorLink = onError(({ graphQLErrors }) => {
  graphQLErrors?.map((error) => {
    const errorMsg = `GraphQL Error: ${error.message}`
    console.log(errorMsg)
    Bugsnag.notify(new Error(errorMsg), (event) => {
      event.severity = 'info'
    })
    return error
  })
})

// GraphqlController renders 422 from its CSRF rescue only, so the status alone marks the case.
// The action never ran, so one retry is safe.
export const healXsrfLink = onError(({ networkError, operation, forward }) => {
  const serverError = networkError as ServerError
  if (serverError?.statusCode !== CSRF_REJECTION_STATUS) return
  if (operation.getContext()[XSRF_RETRIED_CONTEXT_KEY]) return

  const token = serverError.response?.headers?.get(XSRF_RESPONSE_HEADER_NAME)
  if (!token) return

  operation.setContext({ [XSRF_RETRIED_CONTEXT_KEY]: true })
  reportXsrfRecovery(operation.operationName)

  return fromPromise(storeXsrfToken(token)).flatMap(() => forward(operation))
})

const authLink = (headerOptions: any) =>
  setContext(async (_, { headers }) => {
    const value = await readXsrfToken()
    return {
      headers: {
        ...headers,
        ...headerOptions,
        [XSRF_REQUEST_HEADER_NAME]: value
      }
    }
  })

const setTokenLink = new ApolloLink((operation, forward) => {
  return asyncMap(forward(operation), async (data) => {
    const context = operation.getContext()
    const {
      response: { headers }
    } = context

    const token = headers.get(XSRF_RESPONSE_HEADER_NAME)
    if (token) {
      await storeXsrfToken(token)
    }

    return data
  })
})

const httpLink = createHttpLink({
  uri: `${baseUrl}/graphql`,
  credentials: 'include'
})

const uploadLink = createUploadLink({
  uri: `${baseUrl}/graphql`,
  credentials: 'include'
})

const transportLink = ApolloLink.split(
  (operation) => operation.getContext().useUploadLink,
  uploadLink,
  httpLink
)

const createLink = (headers: any) =>
  ApolloLink.from([errorLink, healXsrfLink, authLink(headers), setTokenLink, transportLink])

export const possibleTypes: PossibleTypesMap = {
  ProductLineItemInterface: ['LineItem', 'SubItem', 'RecurringLineItem'],
  LineItemInterface: ['LineItem', 'SubItem', 'DeliveryItem', 'RecurringLineItem']
}

const initClient = (headers: any) =>
  new ApolloClient({
    cache: new InMemoryCache({ possibleTypes }),
    link: createLink(headers),
    defaultOptions: {
      query: {
        errorPolicy: 'all'
      },
      watchQuery: {
        errorPolicy: 'all',
        partialRefetch: true
      },
      mutate: {
        errorPolicy: 'all'
      }
    }
  })

export { initClient }
